import { Matrix4, Vector3 } from 'rewild-common';
import { Renderer } from '../../Renderer';
import { PerspectiveCamera } from '../../core/PerspectiveCamera';
import { IRenderGroup } from '../../../types/IRenderGroup';
import { IVisualComponent } from '../../../types/interfaces';
import { SpotLight } from '../../core/lights/SpotLight';
import shader from '../../shaders/shadow-depth.wgsl';
import { isScatterInstanceGroup } from '../../typeGuards';

interface MeshShadowUniforms {
  buffer: GPUBuffer;
  bindGroup: GPUBindGroup;
}

export class SpotLightShadowRenderer {
  /** True when a shadow-casting spot light was found this frame. */
  hasSpotShadow: boolean = false;
  enabled: boolean = true;
  /** Light VP matrix (world-space) — read by EnvironmentUniforms.prepare() to form lightMVPFromView. */
  lightVP: Matrix4;

  private pipeline: GPURenderPipeline;
  private meshUniforms: Map<IVisualComponent, MeshShadowUniforms>;
  // Scratch set reused by the per-frame stale-entry sweep (no per-frame allocation).
  private _liveMeshes = new Set<IVisualComponent>();

  private _lightViewWorld: Matrix4;
  private _lightView: Matrix4;
  private _lightProj: Matrix4;
  private _shadowMVP: Matrix4;
  private _lightPos: Vector3;
  private _lightTarget: Vector3;
  private _lightDir: Vector3;
  private _lightUp: Vector3;
  private _matData: Float32Array;

  constructor() {
    this.lightVP = new Matrix4();
    this.meshUniforms = new Map();
    this._lightViewWorld = new Matrix4();
    this._lightView = new Matrix4();
    this._lightProj = new Matrix4();
    this._shadowMVP = new Matrix4();
    this._lightPos = new Vector3();
    this._lightTarget = new Vector3();
    this._lightDir = new Vector3();
    this._lightUp = new Vector3();
    this._matData = new Float32Array(16);
  }

  init(renderer: Renderer): void {
    const { device } = renderer;

    const module = device.createShaderModule({
      label: 'spot light shadow depth shader',
      code: shader,
    });

    this.pipeline = device.createRenderPipeline({
      label: 'spot light shadow pipeline',
      layout: 'auto',
      vertex: {
        entryPoint: 'vs',
        module,
        buffers: [
          {
            arrayStride: 4 * 3,
            attributes: [{ shaderLocation: 0, offset: 0, format: 'float32x3' }],
          },
        ],
      },
      primitive: {
        topology: 'triangle-list',
        cullMode: 'none',
      },
      depthStencil: {
        depthWriteEnabled: true,
        depthCompare: 'less',
        format: 'depth32float',
      },
    });
  }

  render(
    encoder: GPUCommandEncoder,
    renderList: IRenderGroup[],
    _camera: PerspectiveCamera,
    renderer: Renderer
  ): void {
    // Drop uniform entries for meshes no longer in the caster list — the map
    // would otherwise pin every dead mesh's geometry forever (terrain
    // sculpting replaces a chunk's mesh on every stamp).
    this._sweepStaleMeshUniforms(renderList);

    const spotLight = this.enabled
      ? this._findShadowCastingSpotLight(renderer)
      : null;
    if (!spotLight) {
      this.hasSpotShadow = false;
      return;
    }

    this.hasSpotShadow = true;
    this._computeSpotLightVP(spotLight);

    const shadowAtlas = renderer.shadowAtlas;
    if (!shadowAtlas) return;

    const { device } = renderer;

    for (const item of renderList) {
      if (item.geometry.requiresBuild) continue;
      for (const mesh of item.meshes) {
        this._ensureMeshUniforms(device, mesh);
      }
    }

    for (const item of renderList) {
      if (item.geometry.requiresBuild) continue;
      for (const mesh of item.meshes) {
        const uniforms = this.meshUniforms.get(mesh);
        if (!uniforms) continue;
        this._shadowMVP.multiplyMatrices(
          this.lightVP,
          mesh.transform.matrixWorld
        );
        this._matData.set(this._shadowMVP.elements);
        device.queue.writeBuffer(uniforms.buffer, 0, this._matData.buffer);
      }
    }

    // The directional pass already cleared the entire atlas to 1.0, so load (no re-clear).
    const pass = encoder.beginRenderPass({
      label: 'spot light shadow pass',
      colorAttachments: [],
      depthStencilAttachment: {
        view: shadowAtlas.createView(),
        depthLoadOp: 'load',
        depthStoreOp: 'store',
      },
    });

    // The spot map is the atlas's bottom-right quadrant, one cascade in size.
    const size = renderer.directionalShadowRenderer.cascadeSize;
    pass.setPipeline(this.pipeline);
    pass.setViewport(size, size, size, size, 0, 1);

    for (const item of renderList) {
      if (item.geometry.requiresBuild) continue;

      const geo = item.geometry;
      pass.setVertexBuffer(0, geo.vertexBuffer);
      pass.setIndexBuffer(geo.indexBuffer, 'uint32');
      const numIndices = geo.indices!.length;

      for (const mesh of item.meshes) {
        const uniforms = this.meshUniforms.get(mesh);
        if (!uniforms) continue;
        pass.setBindGroup(0, uniforms.bindGroup);
        pass.drawIndexed(numIndices);
      }
    }

    pass.end();
  }

  dispose(): void {
    for (const { buffer } of this.meshUniforms.values()) {
      buffer.destroy();
    }
    this.meshUniforms.clear();
  }

  private _findShadowCastingSpotLight(renderer: Renderer): SpotLight | null {
    for (const light of renderer.currentRenderList.lights) {
      if (light instanceof SpotLight && light.castShadow) {
        return light as SpotLight;
      }
    }
    return null;
  }

  private _computeSpotLightVP(light: SpotLight): void {
    this._lightPos.copy(light.transform.position);
    this._lightTarget.copy(light.target.position);

    this._lightDir.subVectors(this._lightTarget, this._lightPos).normalize();
    if (Math.abs(this._lightDir.y) > 0.99) {
      this._lightUp.set(1, 0, 0);
    } else {
      this._lightUp.set(0, 1, 0);
    }

    this._lightViewWorld.lookAt(
      this._lightPos,
      this._lightTarget,
      this._lightUp
    );
    this._lightViewWorld.setPosition(
      this._lightPos.x,
      this._lightPos.y,
      this._lightPos.z
    );
    this._lightView.copy(this._lightViewWorld).invert();

    this._makePerspectiveWebGPU(light.outerAngle * 2, 0.1, light.range);
    this.lightVP.multiplyMatrices(this._lightProj, this._lightView);
  }

  /** Perspective matrix using WebGPU depth convention [0, 1], aspect ratio 1:1. */
  private _makePerspectiveWebGPU(
    fovY: number,
    near: number,
    far: number
  ): void {
    const f = 1.0 / Math.tan(fovY * 0.5);
    const rangeInv = 1.0 / (near - far);
    const te = this._lightProj.elements;

    te[0] = f;
    te[4] = 0;
    te[8] = 0;
    te[12] = 0;
    te[1] = 0;
    te[5] = f;
    te[9] = 0;
    te[13] = 0;
    te[2] = 0;
    te[6] = 0;
    te[10] = far * rangeInv;
    te[14] = near * far * rangeInv;
    te[3] = 0;
    te[7] = 0;
    te[11] = -1;
    te[15] = 0;
  }

  // The shadow render list is collected from the full scene (not camera-
  // culled), so membership only changes when a mesh is actually added to or
  // removed from the scene — entries never thrash with camera movement.
  private _sweepStaleMeshUniforms(renderList: IRenderGroup[]): void {
    const live = this._liveMeshes;
    live.clear();
    for (const item of renderList) {
      for (const mesh of item.meshes) live.add(mesh);
    }
    for (const [mesh, uniforms] of this.meshUniforms) {
      if (live.has(mesh)) continue;
      uniforms.buffer.destroy();
      this.meshUniforms.delete(mesh);
    }
    live.clear();
  }

  private _ensureMeshUniforms(device: GPUDevice, mesh: IVisualComponent): void {
    if (this.meshUniforms.has(mesh)) return;

    // Scatter is directional-only. This pipeline draws one copy per caster, so
    // a chunk of instances would come out as a single stray shadow at the chunk
    // origin; skipping here leaves it out of the write and draw loops too.
    // Give it the instanced path from DirectionalShadowRenderer if a spot light
    // ever needs to shadow foliage.
    if (isScatterInstanceGroup(mesh)) return;

    const buffer = device.createBuffer({
      label: 'spot shadow mesh MVP',
      size: 64,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const bindGroup = device.createBindGroup({
      label: 'spot shadow mesh bind group',
      layout: this.pipeline.getBindGroupLayout(0),
      entries: [{ binding: 0, resource: { buffer } }],
    });

    this.meshUniforms.set(mesh, { buffer, bindGroup });
  }
}
